import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type ScopeType = 'PersonalTenant' | 'CommonTenant';
type PlanStatus = 'Enabled' | 'Disabled';
type QuotaMode = 'PreCheckLimit' | 'PostUsageBilling' | 'RetentionPolicy';
type ChargePolicyType = 'FixedDeny' | 'PointDeduct' | 'UpgradeHint' | 'PurchaseAddon' | 'PaidExtension';

interface QuotaPlanChargeRule {
  ruleType?: ChargePolicyType;
  name?: string;
  description?: string;
  billingUnit?: string;
  unitPrice?: string;
  currency?: string;
  effectValue?: string;
  effectUnit?: string;
  recommend?: boolean;
  effectiveDate?: number;
  sort?: number;
  status?: PlanStatus;
  lastUpdateTime?: number;
}

interface QuotaPlanItem {
  itemId?: string;
  planId?: string;
  categoryCode?: string;
  categoryName?: string;
  quotaCode?: string;
  quotaName?: string;
  quotaDescription?: string;
  unit?: string;
  freeLimitValue?: string;
  defaultValue?: string;
  sort?: number;
  quotaMode?: QuotaMode;
  chargePolicyType?: ChargePolicyType;
  status?: PlanStatus;
  version?: number;
  lastUpdateTime?: number;
  chargeRules?: QuotaPlanChargeRule[];
}

interface QuotaPlan {
  planId?: string;
  scopeType?: ScopeType;
  planName?: string;
  planDescription?: string;
  status?: PlanStatus;
  sort?: number;
  lastUpdateTime?: number;
  items?: QuotaPlanItem[];
}

interface QuotaPlanAuditLog {
  createTime?: number;
  quotaCode?: string;
  quotaName?: string;
  fieldCode?: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  impactTenantCount?: number;
  operatorId?: string;
  operatorName?: string;
  version?: number;
}

interface QuotaAuditDiffRow {
  key: string;
  label: string;
  oldValue: string;
  newValue: string;
  changed: boolean;
}

@Component({
  selector: 'quota-template',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzCardModule,
    NzDescriptionsModule,
    NzDrawerModule,
    NzEmptyModule,
    NzIconModule,
    NzModalModule,
    NzPaginationModule,
    NzSpinModule,
    NzTagModule,
    TestIdDirective
  ],
  templateUrl: './quota-template.component.html',
  styleUrls: ['./quota-template.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotaTemplateComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly http = inject(HttpService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);

  loading = false;
  scopeType: ScopeType = 'PersonalTenant';
  planId = '';
  itemId = '';
  detail: QuotaPlanItem | null = null;
  plan: QuotaPlan | null = null;
  isEditingFreeLimit = false;
  isSavingFreeLimit = false;
  freeLimitDraft: string | number | null = '';
  freeLimitError = '';
  isEditingChargeRules = false;
  isSavingChargeRules = false;
  chargeRuleDrafts: QuotaPlanChargeRule[] = [];
  isSavingStatus = false;
  statusDraft: PlanStatus = 'Enabled';
  auditDrawerVisible = false;
  auditDrawerLoading = false;
  auditLogs: QuotaPlanAuditLog[] = [];
  auditPageIndex = 0;
  auditPageSize = 20;
  auditTotalCount = 0;
  auditHasNextPage = false;
  auditRefreshTime = 0;
  private readonly auditFieldOrder = [
    'freeLimitValue',
    'defaultValue',
    'chargePolicyType',
    'quotaMode',
    'status',
    'chargeRules',
    'lastUpdateTime'
  ];

  t(key: string, params?: Record<string, unknown>): string {
    let text = this.i18n.fanyi(key, params);
    Object.entries(params ?? {}).forEach(([paramKey, value]) => {
      text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value ?? ''));
    });
    return text;
  }

  ngOnInit(): void {
    this.scopeType = this.normalizeScopeType(this.route.snapshot.paramMap.get('scopeType'));
    this.planId = this.route.snapshot.paramMap.get('planId') || '';
    this.itemId = this.route.snapshot.paramMap.get('itemId') || '';
    this.detail = null;
    this.plan = null;
    this.statusDraft = 'Enabled';
    this.loadDetail();
  }

  backToList(): void {
    this.router.navigate(['/quota-plans'], {
      queryParams: { scopeType: this.scopeType }
    });
  }

  get scopeLabel(): string {
    return this.scopeType === 'CommonTenant' ? this.t('quota.scope.team') : this.t('quota.scope.personal');
  }

  get planTitle(): string {
    return this.plan?.planName || this.t('quota.detail.title');
  }

  get planSubtitle(): string {
    return this.plan?.planDescription || this.detail?.quotaDescription || this.t('quota.detail.subtitle');
  }

  get backButtonAriaLabel(): string {
    return this.i18n.fanyi('quota.center.back');
  }

  openAuditDrawer(): void {
    if (!this.planId || !this.itemId) {
      return;
    }

    this.resetAuditPagination();
    this.auditDrawerVisible = true;
    this.loadAuditLogs();
  }

  closeAuditDrawer(): void {
    this.auditDrawerVisible = false;
  }

  onAuditPageChange(page: number): void {
    const nextPageIndex = Math.max(page - 1, 0);
    if (this.auditDrawerLoading || nextPageIndex === this.auditPageIndex) {
      return;
    }

    this.auditPageIndex = nextPageIndex;
    this.loadAuditLogs();
  }

  onAuditPageSizeChange(size: number): void {
    if (this.auditDrawerLoading || size === this.auditPageSize) {
      return;
    }

    this.auditPageSize = size;
    this.auditPageIndex = 0;
    this.loadAuditLogs();
  }

  getStatusLabel(status?: PlanStatus): string {
    if (!status) {
      return '--';
    }
    return status === 'Enabled' ? this.t('quota.status.enabled') : this.t('quota.status.disabled');
  }

  getStatusColor(status?: PlanStatus): string {
    return status === 'Enabled' ? 'green' : 'default';
  }

  getNextStatus(status?: PlanStatus): PlanStatus {
    return status === 'Enabled' ? 'Disabled' : 'Enabled';
  }

  getStatusToggleLabel(status?: PlanStatus): string {
    return status === 'Enabled' ? this.t('quota.detail.action.disable-item') : this.t('quota.detail.action.enable-item');
  }

  getQuotaModeLabel(mode?: QuotaMode): string {
    const modeMap: Record<QuotaMode, string> = {
      PreCheckLimit: this.t('quota.mode.pre-check'),
      PostUsageBilling: this.t('quota.mode.post-usage'),
      RetentionPolicy: this.t('quota.mode.retention')
    };

    return mode ? (modeMap[mode] ?? mode) : '--';
  }

  getChargePolicyLabel(type?: ChargePolicyType): string {
    const policyMap: Record<ChargePolicyType, string> = {
      FixedDeny: this.t('quota.policy.fixed'),
      PointDeduct: this.t('quota.policy.deduct-points'),
      UpgradeHint: this.t('quota.policy.upgrade-prompt'),
      PurchaseAddon: this.t('quota.policy.purchase-extension'),
      PaidExtension: this.t('quota.policy.paid-extension')
    };

    return type ? (policyMap[type] ?? type) : '--';
  }

  getChargePolicyColor(type?: ChargePolicyType): string {
    const colorMap: Record<ChargePolicyType, string> = {
      FixedDeny: 'red',
      PointDeduct: 'green',
      UpgradeHint: 'blue',
      PurchaseAddon: 'orange',
      PaidExtension: 'gold'
    };

    return type ? (colorMap[type] ?? 'default') : 'default';
  }

  formatQuotaValue(value?: string, unit?: string): string {
    if (!value && value !== '0') {
      return '--';
    }

    const unitLabel = this.getUnitLabel(unit);
    const formattedValue = this.formatDisplayNumber(value);
    return unitLabel ? `${formattedValue} ${unitLabel}` : formattedValue;
  }

  formatAuditValue(value?: string): string {
    const text = String(value ?? '').trim();
    return text || '--';
  }

  getAuditDiffRows(log: QuotaPlanAuditLog): QuotaAuditDiffRow[] {
    const oldData = this.parseAuditData(log.oldValue);
    const newData = this.parseAuditData(log.newValue);

    if (this.isAuditWarningRuleList(oldData) || this.isAuditWarningRuleList(newData)) {
      return this.getWarningRuleAuditDiffRows(oldData, newData, log.fieldName);
    }

    if (this.isAuditRuleList(oldData) || this.isAuditRuleList(newData)) {
      return this.getChargeRuleAuditDiffRows(oldData, newData, log.fieldName);
    }

    if (!this.isAuditRecord(oldData) || !this.isAuditRecord(newData)) {
      return [
        {
          key: 'value',
          label: log.fieldName || this.t('quota.detail.audit.field-change'),
          oldValue: this.formatAuditValue(log.oldValue),
          newValue: this.formatAuditValue(log.newValue),
          changed: this.formatAuditValue(log.oldValue) !== this.formatAuditValue(log.newValue)
        }
      ];
    }

    const keys = this.getAuditDiffKeys(oldData, newData);

    return keys
      .map(key => {
        const oldValue = this.formatAuditFieldValue(key, oldData[key]);
        const newValue = this.formatAuditFieldValue(key, newData[key]);
        return {
          key,
          label: this.getAuditFieldLabel(key),
          oldValue,
          newValue,
          changed: oldValue !== newValue
        };
      })
      .filter(row => row.changed);
  }

  isPointDeductRule(rule?: QuotaPlanChargeRule): boolean {
    return rule?.ruleType === 'PointDeduct';
  }

  shouldShowEffectValue(rule?: QuotaPlanChargeRule): boolean {
    return !this.isPointDeductRule(rule);
  }

  formatChargeRulePrice(rule?: QuotaPlanChargeRule): string {
    const currency = String(rule?.currency ?? '').trim();
    const unitPrice = this.formatDisplayNumber(rule?.unitPrice);
    const billingUnit = String(rule?.billingUnit ?? '').trim() || '--';
    const priceText = unitPrice === '--' ? '--' : [currency, unitPrice].filter(Boolean).join(' ');
    return `${priceText} / ${billingUnit}`;
  }

  formatChargeRuleEffectSummary(rule?: QuotaPlanChargeRule): string {
    const effectValue = String(rule?.effectValue ?? '').trim();
    const effectUnit = String(rule?.effectUnit ?? '').trim();
    if (!effectValue) {
      return '--';
    }

    if (effectUnit.toUpperCase() === 'DAY') {
      return this.t('quota.detail.retention-extension-days', { days: effectValue });
    }

    return effectUnit ? `${effectValue} ${effectUnit}` : effectValue;
  }

  isPriceOnlyEditableRule(rule?: QuotaPlanChargeRule): boolean {
    return rule?.ruleType === 'PurchaseAddon' || rule?.ruleType === 'PaidExtension';
  }

  getChargeRuleEffectiveDateInputValue(rule?: QuotaPlanChargeRule): string {
    return this.formatDateOnly(rule?.effectiveDate) === '--' ? '' : this.formatDateOnly(rule?.effectiveDate);
  }

  updateChargeRuleEffectiveDate(rule: QuotaPlanChargeRule, value: string): void {
    if (!value) {
      rule.effectiveDate = undefined;
      return;
    }

    const timestamp = new Date(`${value}T00:00:00`).getTime();
    rule.effectiveDate = Number.isNaN(timestamp) ? undefined : timestamp;
  }

  startEditFreeLimit(): void {
    if (!this.detail || this.isSavingFreeLimit) {
      return;
    }

    this.isEditingFreeLimit = true;
    this.freeLimitDraft = this.detail.freeLimitValue ?? '';
    this.freeLimitError = '';
    this.cdr.markForCheck();
  }

  cancelEditFreeLimit(): void {
    if (this.isSavingFreeLimit) {
      return;
    }

    this.isEditingFreeLimit = false;
    this.freeLimitDraft = '';
    this.freeLimitError = '';
    this.cdr.markForCheck();
  }

  confirmSaveFreeLimit(): void {
    if (!this.detail || !this.planId || !this.itemId || this.isSavingFreeLimit) {
      return;
    }

    const nextFreeLimitValue = this.normalizeFreeLimitDraft(this.freeLimitDraft);
    const validationMessage = this.validateFreeLimitValue(nextFreeLimitValue);
    if (validationMessage) {
      this.freeLimitError = validationMessage;
      this.cdr.markForCheck();
      return;
    }

    this.freeLimitError = '';
    this.cdr.markForCheck();

    const currentValue = this.formatQuotaValue(this.detail.freeLimitValue, this.detail.unit);
    const nextValue = this.formatQuotaValue(nextFreeLimitValue, this.detail.unit);
    this.modal.confirm({
      nzClassName: 'quota-confirm-modal',
      nzTitle: this.t('quota.detail.confirm.save-free-limit-title'),
      nzContent: [
        `${this.t('quota.detail.confirm.current-value')}${currentValue}`,
        `${this.t('quota.detail.confirm.next-value')}${nextValue}`,
        this.t('quota.detail.confirm.impact-range', { scope: this.scopeLabel }),
        this.t('quota.detail.confirm.impact-hint')
      ].join('\n'),
      nzOkText: this.t('quota.modal.save'),
      nzCancelText: this.t('common.cancel'),
      nzOnOk: () => this.saveFreeLimit(nextFreeLimitValue)
    });
  }

  saveFreeLimit(nextFreeLimitValue: string = this.normalizeFreeLimitDraft(this.freeLimitDraft)): void {
    if (!this.detail || !this.planId || !this.itemId || !this.detail.itemId || this.isSavingFreeLimit) {
      return;
    }

    const payload = this.buildQuotaItemUpdatePayload({
      freeLimitValue: nextFreeLimitValue
    });
    if (!payload) {
      return;
    }

    this.isSavingFreeLimit = true;
    this.cdr.markForCheck();

    this.http
      .post(ConstantUtil.ADMIN_QUOTA_PLAN_ITEM_V1_UPDATE, payload)
      .pipe(
        finalize(() => {
          this.isSavingFreeLimit = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('common.save-failed'));
            return;
          }

          this.applyFreeLimitValue(nextFreeLimitValue);
          this.isEditingFreeLimit = false;
          this.freeLimitDraft = '';
          this.message.success(this.t('quota.detail.message.free-limit-saved'));
        },
        error: () => {
          this.message.error(this.i18n.fanyi('common.save-failed'));
        }
      });
  }

  confirmToggleItemStatus(): void {
    if (!this.detail || this.isSavingStatus) {
      return;
    }

    const nextStatus = this.getNextStatus(this.detail.status);
    const actionLabel = nextStatus === 'Enabled' ? this.t('quota.detail.action.enable-item') : this.t('quota.detail.action.disable-item');
    const riskHint =
      nextStatus === 'Enabled' ? this.t('quota.detail.confirm.enable-item-hint') : this.t('quota.detail.confirm.disable-item-hint');
    this.modal.confirm({
      nzClassName: 'quota-confirm-modal',
      nzTitle: actionLabel,
      nzContent: [
        `${this.t('quota.detail.item-name')}${this.detail.quotaName || '--'}`,
        `${this.t('quota.table.code')}：${this.detail.quotaCode || '--'}`,
        `${this.t('quota.detail.current-status')}${this.getStatusLabel(this.detail.status)}`,
        `${this.t('quota.detail.next-status')}${this.getStatusLabel(nextStatus)}`,
        riskHint
      ].join('\n'),
      nzOkText: actionLabel,
      nzCancelText: this.t('common.cancel'),
      nzOkDanger: nextStatus === 'Disabled',
      nzOnOk: () => this.saveItemStatus(nextStatus)
    });
  }

  saveItemStatus(nextStatus: PlanStatus = this.statusDraft): void {
    if (!this.detail || !this.planId || !this.itemId || !this.detail.itemId || this.isSavingStatus || this.detail.status === nextStatus) {
      return;
    }

    this.statusDraft = nextStatus;
    this.isSavingStatus = true;
    this.cdr.markForCheck();

    this.http
      .post(ConstantUtil.ADMIN_QUOTA_PLAN_ITEM_V1_UPDATE, {
        itemId: this.detail.itemId,
        status: nextStatus
      })
      .pipe(
        finalize(() => {
          this.isSavingStatus = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('common.save-failed'));
            return;
          }

          this.applyItemStatus(nextStatus);
          this.message.success(this.t('quota.detail.message.status-saved'));
        },
        error: () => {
          this.message.error(this.i18n.fanyi('common.save-failed'));
        }
      });
  }

  formatDate(timestamp?: number): string {
    if (!timestamp) {
      return '--';
    }

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return '--';
    }

    return new Intl.DateTimeFormat(this.i18n.currentLang || undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByAuditLog(index: number, log: QuotaPlanAuditLog): string {
    return `${log.createTime ?? 0}-${log.fieldCode ?? 'field'}-${log.version ?? 'v'}-${index}`;
  }

  trackByAuditDiff(_: number, row: QuotaAuditDiffRow): string {
    return row.key;
  }

  startEditChargeRules(): void {
    if (!this.detail || this.isSavingChargeRules) {
      return;
    }

    this.isEditingChargeRules = true;
    this.chargeRuleDrafts = this.cloneChargeRules(this.detail.chargeRules);
    this.cdr.markForCheck();
  }

  cancelEditChargeRules(): void {
    if (this.isSavingChargeRules) {
      return;
    }

    this.isEditingChargeRules = false;
    this.chargeRuleDrafts = [];
    this.cdr.markForCheck();
  }

  saveChargeRules(): void {
    if (!this.detail?.itemId || this.isSavingChargeRules) {
      return;
    }

    const chargeRules = this.buildChargeRulesForSave();
    const changedRules = this.getChangedChargeRules(chargeRules);
    if (!changedRules.length) {
      this.cancelEditChargeRules();
      return;
    }

    this.modal.confirm({
      nzClassName: 'quota-confirm-modal',
      nzTitle: this.t('quota.detail.confirm.save-charge-rules-title'),
      nzContent: this.buildChargeRuleConfirmContent(changedRules),
      nzOkText: this.t('common.save'),
      nzCancelText: this.t('common.cancel'),
      nzOnOk: () => this.submitChargeRules(chargeRules)
    });
  }

  private submitChargeRules(chargeRules: QuotaPlanChargeRule[]): void {
    if (!this.detail?.itemId || this.isSavingChargeRules) {
      return;
    }

    this.isSavingChargeRules = true;
    this.cdr.markForCheck();

    this.http
      .post(ConstantUtil.ADMIN_QUOTA_PLAN_ITEM_V1_UPDATE, {
        itemId: this.detail.itemId,
        chargeRules
      })
      .pipe(
        finalize(() => {
          this.isSavingChargeRules = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('common.save-failed'));
            return;
          }

          this.applyChargeRules(chargeRules);
          this.isEditingChargeRules = false;
          this.chargeRuleDrafts = [];
          this.message.success(this.t('quota.detail.message.charge-rules-saved'));
        },
        error: () => {
          this.message.error(this.i18n.fanyi('common.save-failed'));
        }
      });
  }

  private loadDetail(): void {
    if (!this.planId || !this.itemId) {
      this.message.error(this.t('quota.detail.message.missing-params'));
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.http
      .post(ConstantUtil.ADMIN_QUOTA_PLAN_V1_ITEM, { planId: this.planId })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.detail = null;
            this.message.error(res?.msg ?? this.i18n.fanyi('common.load-failed'));
            return;
          }

          this.plan = this.normalizePlan(res.data ?? null);
          const matched =
            this.plan?.items?.find((item: QuotaPlanItem) => item?.itemId === this.itemId && item?.planId === this.planId) ??
            this.plan?.items?.find((item: QuotaPlanItem) => item?.itemId === this.itemId) ??
            null;
          this.detail = this.normalizeDetail(matched);
          this.statusDraft = this.detail?.status ?? 'Enabled';
          this.cancelEditFreeLimit();
          this.cancelEditChargeRules();
          if (!this.detail) {
            this.message.error(this.t('quota.detail.not-found'));
          }
        },
        error: () => {
          this.detail = null;
          this.message.error(this.i18n.fanyi('common.load-failed'));
        }
      });
  }

  private loadAuditLogs(): void {
    this.auditDrawerLoading = true;
    this.auditLogs = [];
    this.cdr.markForCheck();

    this.http
      .post(ConstantUtil.ADMIN_QUOTA_PLAN_V1_AUDIT_LIST, {
        planId: this.planId,
        itemId: this.itemId,
        pageIndex: this.auditPageIndex,
        pageSize: this.auditPageSize,
        orderBy: 'createTime',
        direction: 'desc',
        refreshTime: this.auditRefreshTime
      })
      .pipe(
        finalize(() => {
          this.auditDrawerLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.auditLogs = [];
            this.auditTotalCount = 0;
            this.auditHasNextPage = false;
            this.message.error(res?.msg ?? this.i18n.fanyi('common.load-failed'));
            return;
          }

          const payload = res.data;
          if (Array.isArray(payload)) {
            this.auditLogs = [...payload].sort((a, b) => (b?.createTime ?? 0) - (a?.createTime ?? 0));
            this.auditTotalCount = payload.length;
            this.auditHasNextPage = false;
            return;
          }

          const logs = Array.isArray(payload?.items) ? payload.items : [];
          this.auditLogs = logs;
          this.auditTotalCount = Number(payload?.totalCount ?? logs.length ?? 0);
          this.auditHasNextPage = !!payload?.hasNextPage;
          this.auditPageIndex = Number(payload?.pageIndex ?? this.auditPageIndex ?? 0);
          this.auditRefreshTime = Number(payload?.refreshTime ?? this.auditRefreshTime ?? 0);
        },
        error: () => {
          this.auditLogs = [];
          this.auditTotalCount = 0;
          this.auditHasNextPage = false;
          this.message.error(this.i18n.fanyi('common.load-failed'));
        }
      });
  }

  private resetAuditPagination(): void {
    this.auditPageIndex = 0;
    this.auditPageSize = 20;
    this.auditTotalCount = 0;
    this.auditHasNextPage = false;
    this.auditRefreshTime = 0;
    this.auditLogs = [];
  }

  private normalizeScopeType(scopeType?: string | null): ScopeType {
    return scopeType === 'CommonTenant' || scopeType === 'COMMON_TENANT' ? 'CommonTenant' : 'PersonalTenant';
  }

  private normalizeFreeLimitDraft(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private parseAuditData(value?: string): unknown {
    let current: unknown = String(value ?? '').trim();
    if (!current) {
      return null;
    }

    for (let i = 0; i < 3; i += 1) {
      if (typeof current !== 'string') {
        return current;
      }

      const text = current.trim();
      if (!text) {
        return null;
      }

      const looksLikeJson =
        (text.startsWith('{') && text.endsWith('}')) ||
        (text.startsWith('[') && text.endsWith(']')) ||
        (text.startsWith('"[') && text.endsWith(']"')) ||
        (text.startsWith('"{') && text.endsWith('}"'));

      if (!looksLikeJson) {
        return text;
      }

      try {
        current = JSON.parse(text);
      } catch {
        return text;
      }
    }

    return current;
  }

  private getAuditDiffKeys(oldData: Record<string, unknown>, newData: Record<string, unknown>): string[] {
    const keys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
    const ordered = this.auditFieldOrder.filter(key => keys.has(key));
    const extra = [...keys].filter(key => key !== 'version' && !this.auditFieldOrder.includes(key)).sort((a, b) => a.localeCompare(b));
    return [...ordered, ...extra];
  }

  private getAuditFieldLabel(key: string): string {
    const labelMap: Record<string, string> = {
      freeLimitValue: this.t('quota.table.free-quota'),
      defaultValue: this.t('quota.detail.default-value'),
      chargePolicyType: this.t('quota.table.overflow-policy'),
      quotaMode: this.t('quota.detail.quota-mode'),
      chargeRules: this.t('quota.detail.audit.charge-rule'),
      status: this.t('quota.table.status'),
      version: this.t('quota.detail.audit.version'),
      lastUpdateTime: this.t('quota.detail.updated-at')
    };

    return labelMap[key] ?? key;
  }

  private formatAuditFieldValue(key: string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }

    if (key === 'freeLimitValue' || key === 'defaultValue') {
      return this.formatQuotaValue(String(value), this.detail?.unit);
    }

    if (key === 'chargePolicyType') {
      return this.getChargePolicyLabel(String(value) as ChargePolicyType);
    }

    if (key === 'quotaMode') {
      return this.getQuotaModeLabel(String(value) as QuotaMode);
    }

    if (key === 'status') {
      return this.getStatusLabel(String(value) as PlanStatus);
    }

    if (key === 'lastUpdateTime') {
      const timestamp = Number(value);
      return Number.isFinite(timestamp) ? this.formatDate(timestamp) : String(value);
    }

    return String(value);
  }

  private isAuditRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private isAuditRuleList(value: unknown): value is Array<Record<string, unknown>> {
    return Array.isArray(value);
  }

  private isAuditWarningRuleList(value: unknown): value is Array<Record<string, unknown>> {
    return (
      Array.isArray(value) &&
      value.some(item => this.isAuditRecord(item) && ('thresholdPercent' in item || 'titleTemplate' in item || 'contentTemplate' in item))
    );
  }

  private getChargeRuleAuditDiffRows(oldValue: unknown, newValue: unknown, fieldName?: string): QuotaAuditDiffRow[] {
    const oldRules = Array.isArray(oldValue) ? oldValue : [];
    const newRules = Array.isArray(newValue) ? newValue : [];
    const fieldOrder = [
      'name',
      'ruleType',
      'description',
      'unitPrice',
      'currency',
      'billingUnit',
      'effectValue',
      'effectUnit',
      'effectiveDate',
      'status',
      'recommend'
    ];

    return Array.from({ length: Math.max(oldRules.length, newRules.length) }, (_, index) => {
      const oldRule = this.isAuditRecord(oldRules[index]) ? oldRules[index] : {};
      const newRule = this.isAuditRecord(newRules[index]) ? newRules[index] : {};
      const ruleName = String(
        newRule['name'] ?? oldRule['name'] ?? `${fieldName || this.t('quota.detail.audit.charge-rule')} ${index + 1}`
      ).trim();
      return fieldOrder
        .map(key => {
          const oldFieldValue = this.formatChargeRuleAuditValue(key, oldRule[key]);
          const newFieldValue = this.formatChargeRuleAuditValue(key, newRule[key]);
          return {
            key: `chargeRules-${index}-${key}`,
            label: `${ruleName} / ${this.getChargeRuleAuditFieldLabel(key)}`,
            oldValue: oldFieldValue,
            newValue: newFieldValue,
            changed: oldFieldValue !== newFieldValue
          };
        })
        .filter(row => row.changed);
    }).flat();
  }

  private getWarningRuleAuditDiffRows(oldValue: unknown, newValue: unknown, fieldName?: string): QuotaAuditDiffRow[] {
    const oldRules = Array.isArray(oldValue) ? oldValue : [];
    const newRules = Array.isArray(newValue) ? newValue : [];
    const fieldOrder = ['thresholdPercent', 'titleTemplate', 'contentTemplate', 'status'];

    return Array.from({ length: Math.max(oldRules.length, newRules.length) }, (_, index) => {
      const oldRule = this.isAuditRecord(oldRules[index]) ? oldRules[index] : {};
      const newRule = this.isAuditRecord(newRules[index]) ? newRules[index] : {};
      const threshold = String(newRule['thresholdPercent'] ?? oldRule['thresholdPercent'] ?? `${index + 1}`).trim();
      const ruleName = `${fieldName || this.t('quota.detail.audit.warning-rule')} ${threshold}%`;
      return fieldOrder
        .map(key => {
          const oldFieldValue = this.formatWarningRuleAuditValue(key, oldRule[key]);
          const newFieldValue = this.formatWarningRuleAuditValue(key, newRule[key]);
          return {
            key: `warningRules-${index}-${key}`,
            label: `${ruleName} / ${this.getWarningRuleAuditFieldLabel(key)}`,
            oldValue: oldFieldValue,
            newValue: newFieldValue,
            changed: oldFieldValue !== newFieldValue
          };
        })
        .filter(row => row.changed);
    }).flat();
  }

  private getChargeRuleAuditFieldLabel(key: string): string {
    const labelMap: Record<string, string> = {
      name: this.t('quota.detail.audit.charge-field.name'),
      ruleType: this.t('quota.detail.audit.charge-field.rule-type'),
      description: this.t('quota.detail.audit.charge-field.description'),
      unitPrice: this.t('quota.detail.audit.charge-field.unit-price'),
      currency: this.t('quota.detail.audit.charge-field.currency'),
      billingUnit: this.t('quota.detail.audit.charge-field.billing-unit'),
      effectValue: this.t('quota.detail.audit.charge-field.effect-value'),
      effectUnit: this.t('quota.detail.audit.charge-field.effect-unit'),
      effectiveDate: this.t('quota.detail.audit.charge-field.effective-date'),
      status: this.t('quota.detail.audit.charge-field.status'),
      recommend: this.t('quota.detail.audit.charge-field.recommend')
    };

    return labelMap[key] ?? key;
  }

  private formatChargeRuleAuditValue(key: string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }

    if (key === 'ruleType') {
      return this.getChargePolicyLabel(String(value) as ChargePolicyType);
    }

    if (key === 'unitPrice') {
      return this.formatDisplayNumber(String(value));
    }

    if (key === 'effectiveDate') {
      return this.formatDate(Number(value));
    }

    if (key === 'status') {
      return this.getStatusLabel(String(value) as PlanStatus);
    }

    if (key === 'recommend') {
      return value === true || String(value).toLowerCase() === 'true' ? this.t('common.yes') : this.t('common.no');
    }

    return String(value);
  }

  private getWarningRuleAuditFieldLabel(key: string): string {
    const labelMap: Record<string, string> = {
      thresholdPercent: this.t('quota.detail.audit.warning-field.threshold'),
      titleTemplate: this.t('quota.detail.audit.warning-field.title'),
      contentTemplate: this.t('quota.detail.audit.warning-field.content'),
      status: this.t('quota.detail.audit.warning-field.status')
    };

    return labelMap[key] ?? key;
  }

  private formatWarningRuleAuditValue(key: string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }

    if (key === 'thresholdPercent') {
      return `${this.formatDisplayNumber(String(value))}%`;
    }

    if (key === 'status') {
      return this.getStatusLabel(String(value) as PlanStatus);
    }

    return String(value);
  }

  private cloneChargeRules(chargeRules?: QuotaPlanChargeRule[] | null): QuotaPlanChargeRule[] {
    return (chargeRules ?? []).map(rule => ({ ...rule }));
  }

  private normalizeChargeRules(chargeRules?: QuotaPlanChargeRule[] | null): QuotaPlanChargeRule[] {
    return this.cloneChargeRules(chargeRules).map(rule => ({
      ...rule,
      name: String(rule.name ?? '').trim(),
      description: String(rule.description ?? '').trim(),
      billingUnit: String(rule.billingUnit ?? '').trim(),
      unitPrice: String(rule.unitPrice ?? '').trim(),
      currency: String(rule.currency ?? '').trim(),
      effectValue: String(rule.effectValue ?? '').trim(),
      effectUnit: String(rule.effectUnit ?? '').trim()
    }));
  }

  private buildChargeRulesForSave(): QuotaPlanChargeRule[] {
    const currentRules = this.detail?.chargeRules ?? [];
    const draftRules = this.normalizeChargeRules(this.chargeRuleDrafts);

    return draftRules.map((rule, index) => this.applyDerivedChargeRuleFields(rule, currentRules[index]));
  }

  private applyDerivedChargeRuleFields(rule: QuotaPlanChargeRule, originalRule?: QuotaPlanChargeRule): QuotaPlanChargeRule {
    if (!this.isPointDeductRule(rule)) {
      return rule;
    }

    const nextPrice = String(rule.unitPrice ?? '').trim();
    const originalPrice = String(originalRule?.unitPrice ?? '').trim();
    const originalDescription = String(originalRule?.description ?? rule.description ?? '').trim();
    let nextDescription = originalDescription;

    if (originalDescription && originalPrice && nextPrice && originalPrice !== nextPrice) {
      nextDescription = originalDescription.replace(originalPrice, nextPrice);
    }

    return {
      ...rule,
      effectValue: '',
      effectUnit: '',
      description: nextDescription || rule.description || ''
    };
  }

  private getChangedChargeRules(nextRules: QuotaPlanChargeRule[]): QuotaPlanChargeRule[] {
    const currentRules = this.detail?.chargeRules ?? [];

    return nextRules.filter((rule, index) => {
      const currentRule = currentRules[index];
      return (
        String(rule.unitPrice ?? '') !== String(currentRule?.unitPrice ?? '') ||
        String(rule.effectValue ?? '') !== String(currentRule?.effectValue ?? '') ||
        String(rule.effectiveDate ?? '') !== String(currentRule?.effectiveDate ?? '') ||
        String(rule.description ?? '') !== String(currentRule?.description ?? '')
      );
    });
  }

  private buildChargeRuleConfirmContent(changedRules: QuotaPlanChargeRule[]): string {
    const quotaName = this.detail?.quotaName || '--';

    return changedRules
      .map(rule => {
        const effectiveDate = this.formatDateOnly(rule.effectiveDate);
        if (effectiveDate !== '--') {
          return this.t('quota.detail.confirm.charge-rule-effective-date', {
            quotaName,
            effectiveDate
          });
        }

        return this.t('quota.detail.confirm.charge-rule-generic', {
          quotaName
        });
      })
      .join('\n');
  }

  private normalizeDetail(detail?: QuotaPlanItem | null): QuotaPlanItem | null {
    if (!detail) {
      return null;
    }

    return {
      ...detail,
      chargeRules: this.normalizeChargeRules([...(detail.chargeRules ?? [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)))
    };
  }

  private normalizePlan(plan?: QuotaPlan | null): QuotaPlan | null {
    if (!plan) {
      return null;
    }

    return {
      ...plan,
      items: [...(plan.items ?? [])].map(item => this.normalizeDetail(item) as QuotaPlanItem).sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    };
  }

  private validateFreeLimitValue(value: string): string | null {
    if (!value) {
      return this.t('quota.detail.validation.free-limit-required');
    }

    if (!/^-?\d+$/.test(value)) {
      return this.t('quota.detail.validation.free-limit-integer');
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return this.t('quota.detail.validation.free-limit-number');
    }

    if (numericValue < 0) {
      return this.t('quota.detail.validation.free-limit-min');
    }

    if (numericValue > 999999) {
      return this.t('quota.detail.validation.free-limit-max');
    }

    return null;
  }

  private getUnitLabel(unit?: string): string {
    const normalizedUnit = String(unit ?? '')
      .trim()
      .toLowerCase();
    const unitMap: Record<string, string> = {
      个: this.t('quota.unit.item'),
      item: this.t('quota.unit.item'),
      items: this.t('quota.unit.item'),
      人: this.t('quota.unit.person'),
      person: this.t('quota.unit.person'),
      people: this.t('quota.unit.person'),
      天: this.t('quota.unit.day'),
      day: this.t('quota.unit.day'),
      days: this.t('quota.unit.day'),
      组: this.t('quota.unit.group'),
      group: this.t('quota.unit.group'),
      groups: this.t('quota.unit.group'),
      gb: this.t('quota.unit.gb')
    };

    return unitMap[normalizedUnit] ?? (unit?.trim() || '');
  }

  private formatDisplayNumber(value?: string | number | null): string {
    const text = String(value ?? '').trim();
    if (!text) {
      return '--';
    }

    const normalizedText = text.replace(/,/g, '');
    const num = Number(normalizedText);
    if (!Number.isFinite(num) || !/^-?\d+(\.\d+)?$/.test(normalizedText)) {
      return text;
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  }

  private formatDateOnly(timestamp?: number): string {
    if (!timestamp) {
      return '--';
    }

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return '--';
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private applyFreeLimitValue(value: string): void {
    if (!this.detail) {
      return;
    }

    const quotaDescription = this.buildQuotaDescription({
      ...this.detail,
      freeLimitValue: value
    });
    this.detail = {
      ...this.detail,
      freeLimitValue: value,
      quotaDescription
    };

    if (this.plan) {
      this.plan = {
        ...this.plan,
        items: [...(this.plan.items ?? [])].map(item =>
          item.itemId === this.detail?.itemId
            ? {
                ...item,
                freeLimitValue: value,
                quotaDescription
              }
            : item
        )
      };
    }
  }

  private buildQuotaItemUpdatePayload(
    changes: Partial<Pick<QuotaPlanItem, 'freeLimitValue' | 'chargePolicyType'>>
  ): ({ itemId: string; quotaDescription: string } & Partial<Pick<QuotaPlanItem, 'freeLimitValue' | 'chargePolicyType'>>) | null {
    if (!this.detail?.itemId) {
      return null;
    }

    const nextItem: QuotaPlanItem = {
      ...this.detail,
      ...changes
    };
    const payload: { itemId: string; quotaDescription: string } & Partial<Pick<QuotaPlanItem, 'freeLimitValue' | 'chargePolicyType'>> = {
      itemId: this.detail.itemId,
      quotaDescription: this.buildQuotaDescription(nextItem)
    };

    if (Object.prototype.hasOwnProperty.call(changes, 'freeLimitValue')) {
      payload.freeLimitValue = nextItem.freeLimitValue;
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'chargePolicyType')) {
      payload.chargePolicyType = nextItem.chargePolicyType;
    }

    return payload;
  }

  private buildQuotaDescription(item: QuotaPlanItem): string {
    const displayValue = this.formatQuotaDescriptionValue(item.freeLimitValue, item.unit);
    switch (item.chargePolicyType) {
      case 'PointDeduct':
        return `免費配額 ${displayValue}，超出後按${this.getDeductMetric(item)}扣 Aile 點`;
      case 'UpgradeHint':
        return `硬性上限 ${displayValue}，超出提示升級團隊版`;
      case 'PurchaseAddon':
        return `免費配額 ${displayValue}，超出後可購買擴展包`;
      case 'PaidExtension':
        return `所有${this.getRetentionObjectName(item.quotaCode)}預設保存 ${displayValue}，可購買延長包`;
      case 'FixedDeny':
        return Number(item.freeLimitValue) === 0 ? `預設 ${displayValue}，固定不可擴展` : `硬性限制，固定為 ${displayValue}`;
      default:
        return item.quotaDescription ?? '';
    }
  }

  private getDeductMetric(item: QuotaPlanItem): string {
    switch (item.quotaCode) {
      case 'EMPLOYEE_LIMIT':
      case 'CUSTOMER_LIMIT':
        return '人數';
      case 'TAG_LIMIT':
      case 'ENTRY_REASON_LIMIT':
      case 'ASSISTANT_LIMIT':
        return '個數';
      case 'STORAGE_SPACE':
        return 'GB';
      case 'BROADCAST_LIMIT':
        return '實際觸達人次';
      default:
        return item.unit?.trim() || '用量';
    }
  }

  private getRetentionObjectName(quotaCode?: string): string {
    switch (quotaCode) {
      case 'MESSAGE_RETENTION':
        return '訊息';
      case 'ATTACHMENT_RETENTION':
        return '附件';
      default:
        return '資料';
    }
  }

  private formatQuotaDescriptionValue(value?: string | number | null, unit?: string | null): string {
    const normalizedValue = String(value ?? '').trim();
    const normalizedUnit = this.normalizeDescriptionUnit(unit);
    if (!normalizedValue) {
      return '--';
    }
    return `${normalizedValue}${normalizedUnit ? ` ${normalizedUnit}` : ''}`;
  }

  private normalizeDescriptionUnit(unit?: string | null): string {
    return String(unit ?? '')
      .trim()
      .replace(/个/g, '個')
      .replace(/条/g, '條');
  }

  private applyItemStatus(status: PlanStatus): void {
    if (!this.detail) {
      return;
    }

    this.detail = {
      ...this.detail,
      status
    };

    if (this.plan) {
      this.plan = {
        ...this.plan,
        items: [...(this.plan.items ?? [])].map(item =>
          item.itemId === this.detail?.itemId
            ? {
                ...item,
                status
              }
            : item
        )
      };
    }
  }

  private applyChargeRules(chargeRules: QuotaPlanChargeRule[]): void {
    if (!this.detail) {
      return;
    }

    const normalizedChargeRules = this.normalizeChargeRules(chargeRules);
    this.detail = {
      ...this.detail,
      chargeRules: normalizedChargeRules
    };

    if (this.plan) {
      this.plan = {
        ...this.plan,
        items: [...(this.plan.items ?? [])].map(item =>
          item.itemId === this.detail?.itemId
            ? {
                ...item,
                chargeRules: normalizedChargeRules
              }
            : item
        )
      };
    }
  }
}
